import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { DocumentVersionStatusSchema } from "../documents/uploads.js";
import { MessageSchema, ReactionSummarySchema } from "../messaging/index.js";
const RealtimeEnvelopeSchema = z.strictObject({
  id: IdSchema,
  type: z.string().min(1).max(100),
  occurredAt: IsoDateTimeSchema,
  data: z.unknown()
});
const RealtimeRoomSchema = z.string().regex(/^(user|channel|space|doc|meeting):[0-9a-f-]{36}$/);
const REALTIME_MAX_ROOMS_PER_SOCKET = 200;
const REALTIME_TYPING_INTERVAL_MS = 3e3;
const PRESENCE_STATUSES = ["available", "busy", "in_meeting", "on_site", "away"];
const PresenceStatusSchema = z.enum(PRESENCE_STATUSES);
const PresenceStateSchema = z.enum([...PRESENCE_STATUSES, "offline"]);
const PRESENCE_MESSAGE_MAX = 80;
const INVISIBLE_RANGES = [
  [0, 31],
  [127, 159],
  [8206, 8207],
  [8234, 8238],
  [8294, 8297]
];
const INVISIBLE_OR_CONTROL = new RegExp(
  `[${INVISIBLE_RANGES.map(([from, to]) => `\\u{${from.toString(16)}}-\\u{${to.toString(16)}}`).join("")}]`,
  "gu"
);
const cleanPresenceMessage = (text) => text.replace(INVISIBLE_OR_CONTROL, " ").replace(/\s+/g, " ").trim();
const PresenceChangedSchema = z.strictObject({
  userId: IdSchema,
  status: PresenceStateSchema,
  /** The person's status message (null: none, and always null while offline). */
  message: z.string().max(PRESENCE_MESSAGE_MAX).nullable()
});
const PresenceSnapshotSchema = z.strictObject({
  items: z.array(PresenceChangedSchema).max(5e3)
});
const RealtimeNotificationCreatedSchema = z.strictObject({ notificationId: IdSchema });
const RealtimeDocumentVersionStatusSchema = z.strictObject({
  spaceId: IdSchema,
  documentId: IdSchema,
  versionId: IdSchema,
  status: DocumentVersionStatusSchema
});
const RealtimeDocumentUpdatedSchema = z.strictObject({
  spaceId: IdSchema,
  documentId: IdSchema
});
const RealtimeApprovalUpdatedSchema = z.strictObject({
  spaceId: IdSchema,
  flowId: IdSchema,
  documentId: IdSchema
});
const RealtimeTaskUpdatedSchema = z.strictObject({
  projectId: IdSchema,
  taskId: IdSchema
});
const RealtimeAccessRevokedSchema = z.strictObject({
  scope: z.enum(["organization", "space", "rooms"]),
  spaceId: IdSchema.nullable(),
  rooms: z.array(RealtimeRoomSchema).max(REALTIME_MAX_ROOMS_PER_SOCKET)
});
const RealtimeSessionRevokedSchema = z.strictObject({});
const RealtimeTypingSchema = z.strictObject({ channelId: IdSchema, userId: IdSchema });
const RealtimeMessageSchema = MessageSchema;
const RealtimeReactionChangedSchema = z.strictObject({
  channelId: IdSchema,
  messageId: IdSchema,
  reactions: z.array(ReactionSummarySchema)
});
const RealtimePinChangedSchema = z.strictObject({
  channelId: IdSchema,
  messageId: IdSchema,
  pinned: z.boolean()
});
const RealtimeChannelReadStateSchema = z.strictObject({
  channelId: IdSchema,
  lastReadMessageId: IdSchema
});
const RealtimeMeetingUpdatedSchema = z.strictObject({
  meetingId: IdSchema,
  status: z.enum(["scheduled", "live", "ended"]),
  participantCount: z.int().min(0)
});
const RealtimeCallIncomingSchema = z.strictObject({
  /** The organisation of the call (a person may belong to several). */
  organizationId: IdSchema,
  meetingId: IdSchema,
  channelId: IdSchema,
  callerId: IdSchema,
  callerName: z.string()
});
const RealtimeEinvoiceStatusChangedSchema = z.strictObject({
  organizationId: IdSchema,
  invoiceId: IdSchema,
  status: z.enum(["signed", "submitted", "accepted", "rejected", "failed", "manual"])
});
const serverToClientEvents = {
  "notification.created": RealtimeNotificationCreatedSchema,
  "document.version.status_changed": RealtimeDocumentVersionStatusSchema,
  "document.updated": RealtimeDocumentUpdatedSchema,
  "approval.updated": RealtimeApprovalUpdatedSchema,
  "task.updated": RealtimeTaskUpdatedSchema,
  "access.revoked": RealtimeAccessRevokedSchema,
  "session.revoked": RealtimeSessionRevokedSchema,
  "presence.changed": PresenceChangedSchema,
  "presence.snapshot": PresenceSnapshotSchema,
  "message.created": RealtimeMessageSchema,
  "message.updated": RealtimeMessageSchema,
  "message.deleted": RealtimeMessageSchema,
  "reaction.changed": RealtimeReactionChangedSchema,
  "pin.changed": RealtimePinChangedSchema,
  "channel.read_state": RealtimeChannelReadStateSchema,
  typing: RealtimeTypingSchema,
  "meeting.updated": RealtimeMeetingUpdatedSchema,
  "call.incoming": RealtimeCallIncomingSchema,
  "einvoice.status_changed": RealtimeEinvoiceStatusChangedSchema
};
const SubscribeRequestSchema = z.strictObject({ room: RealtimeRoomSchema });
const TypingRequestSchema = z.strictObject({ channelId: IdSchema });
const PresenceSetRequestSchema = z.strictObject({
  status: PresenceStatusSchema,
  message: z.string().max(200).transform(cleanPresenceMessage).pipe(z.string().max(PRESENCE_MESSAGE_MAX)).nullable().optional()
});
const clientToServerEvents = {
  subscribe: SubscribeRequestSchema,
  unsubscribe: SubscribeRequestSchema,
  typing: TypingRequestSchema,
  "presence.set": PresenceSetRequestSchema
};
const SubscribeAckSchema = z.discriminatedUnion("ok", [
  z.strictObject({ ok: z.literal(true) }),
  z.strictObject({ ok: z.literal(false), code: z.enum(["invalid", "not_allowed", "limit"]) })
]);
export {
  PRESENCE_MESSAGE_MAX,
  PRESENCE_STATUSES,
  PresenceChangedSchema,
  PresenceSetRequestSchema,
  PresenceSnapshotSchema,
  PresenceStateSchema,
  PresenceStatusSchema,
  REALTIME_MAX_ROOMS_PER_SOCKET,
  REALTIME_TYPING_INTERVAL_MS,
  RealtimeAccessRevokedSchema,
  RealtimeApprovalUpdatedSchema,
  RealtimeCallIncomingSchema,
  RealtimeChannelReadStateSchema,
  RealtimeDocumentUpdatedSchema,
  RealtimeDocumentVersionStatusSchema,
  RealtimeEinvoiceStatusChangedSchema,
  RealtimeEnvelopeSchema,
  RealtimeMeetingUpdatedSchema,
  RealtimeMessageSchema,
  RealtimeNotificationCreatedSchema,
  RealtimePinChangedSchema,
  RealtimeReactionChangedSchema,
  RealtimeRoomSchema,
  RealtimeSessionRevokedSchema,
  RealtimeTaskUpdatedSchema,
  RealtimeTypingSchema,
  SubscribeAckSchema,
  SubscribeRequestSchema,
  TypingRequestSchema,
  cleanPresenceMessage,
  clientToServerEvents,
  serverToClientEvents
};
//# sourceMappingURL=index.js.map